import { useEffect, useRef } from "react";

/**
 * Lightweight, dependency-free rich-text editor for the release-notes document.
 * A contenteditable surface plus a toolbar (version/section headings, lists, a rule, bold /
 * italic, links, and a "New release" template). Uses document.execCommand - long deprecated
 * but still supported everywhere and perfect for a small internal editor with no external
 * dependencies (which also keeps the console's strict CSP happy).
 *
 * The editor body is styled to match audioswytch.co.uk/release-notes exactly (see
 * .rte-body in styles.css): a Version heading (H2) draws the separator line above itself,
 * the paragraph right after it is the date, a Section heading (H3) is the muted label,
 * bullets are plain. What you see here is what the website shows.
 */
export function RichTextEditor({
  initialHtml,
  onChange,
}: {
  initialHtml: string;
  onChange: (html: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const seeded = useRef(false);

  useEffect(() => {
    if (ref.current && !seeded.current) {
      ref.current.innerHTML = initialHtml || "<p></p>";
      normalize(ref.current);
      seeded.current = true;
      // Hand back the cleaned-up markup straight away so a Save without further edits
      // publishes the tidy version of whatever was stored before.
      onChange(ref.current.innerHTML);
    }
  }, [initialHtml, onChange]);

  function emit() {
    if (!ref.current) return;
    normalize(ref.current);
    onChange(ref.current.innerHTML);
  }

  function run(command: string, value?: string) {
    ref.current?.focus();
    document.execCommand(command, false, value);
    emit();
  }

  function addLink() {
    const url = window.prompt("Link URL (https://…)")?.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url) && !url.startsWith("mailto:")) {
      window.alert("Enter a full URL starting with https:// (or a mailto: address).");
      return;
    }
    run("createLink", url);
  }

  /** Inserts a complete release block at the TOP of the document (newest first), ready to fill in. */
  function newRelease() {
    const version = window.prompt("Version number (e.g. 1.0.3)")?.trim();
    if (!version) return;
    const date = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    const block =
      "<h2>" + escapeHtml(version) + "</h2>" +
      "<p><em>" + date + "</em></p>" +
      "<h3>Fixed</h3><ul><li>What was fixed, in one line.</li></ul>" +
      "<h3>Changed</h3><ul><li>What changed, in one line.</li></ul>";
    if (ref.current) {
      const current = ref.current.innerHTML.trim();
      ref.current.innerHTML = block + (current && current !== "<p></p>" ? current : "");
      ref.current.focus();
      emit();
    }
  }

  // preventDefault on mousedown keeps the editor's text selection while a toolbar
  // button is clicked, so the command applies to what the user had selected.
  const keep = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className="rte">
      <div className="rte-toolbar">
        <button type="button" className="rte-primary" onMouseDown={keep} onClick={newRelease} title="Insert a new version block at the top">
          + New release
        </button>
        <span className="rte-sep" />
        <button type="button" onMouseDown={keep} onClick={() => run("formatBlock", "H2")} title="Version heading - draws the separator line on the website">Version</button>
        <button type="button" onMouseDown={keep} onClick={() => run("formatBlock", "H3")} title="Section label (Fixed, Changed…)">Section</button>
        <button type="button" onMouseDown={keep} onClick={() => run("formatBlock", "P")} title="Normal text">Text</button>
        <span className="rte-sep" />
        <button type="button" onMouseDown={keep} onClick={() => run("insertUnorderedList")} title="Bullet list">• List</button>
        <button type="button" onMouseDown={keep} onClick={() => run("insertOrderedList")} title="Numbered list">1. List</button>
        <button type="button" onMouseDown={keep} onClick={() => run("insertHorizontalRule")} title="Separator line">― Rule</button>
        <span className="rte-sep" />
        <button type="button" onMouseDown={keep} onClick={() => run("bold")} title="Bold"><b>B</b></button>
        <button type="button" onMouseDown={keep} onClick={() => run("italic")} title="Italic"><i>I</i></button>
        <button type="button" onMouseDown={keep} onClick={addLink} title="Insert link">Link</button>
        <button type="button" onMouseDown={keep} onClick={() => run("removeFormat")} className="rte-clear" title="Clear formatting">Clear</button>
      </div>
      <div
        ref={ref}
        className="rte-body"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        role="textbox"
        aria-multiline="true"
        aria-label="Release notes content"
      />
    </div>
  );
}

const BLOCKS = new Set(["P", "UL", "OL", "DIV", "H2", "H3", "HR"]);

/**
 * Tidies contenteditable output into the simple document the website expects, without moving the
 * caret's text: headings that ended up wrapping whole blocks (what a paste inside a heading does)
 * are unwrapped, <div> wrappers become paragraphs, inline styles and empty paragraphs go. Runs on
 * load and after every edit, so what is saved is always the clean structure.
 */
function normalize(root: HTMLElement): void {
  // Unwrap headings/paragraphs that contain block children (repeat until stable - nesting can be deep).
  for (let pass = 0; pass < 5; pass++) {
    let changed = false;
    root.querySelectorAll("h2, h3, p").forEach((el) => {
      if (Array.from(el.children).some((c) => BLOCKS.has(c.tagName))) {
        el.replaceWith(...Array.from(el.childNodes));
        changed = true;
      }
    });
    if (!changed) break;
  }
  // <div> as a paragraph (browsers insert divs on Enter).
  root.querySelectorAll("div").forEach((div) => {
    const p = document.createElement("p");
    p.append(...Array.from(div.childNodes));
    div.replaceWith(p);
  });
  // Inline styles/classes carry nothing the site uses.
  root.querySelectorAll("[style], [class]").forEach((el) => {
    el.removeAttribute("style");
    el.removeAttribute("class");
  });
  // <b>/<strong> wrapping a whole date line is just the paste's formatting - keep the <em> only.
  root.querySelectorAll("p > b > em:only-child, p > strong > em:only-child").forEach((em) => {
    em.parentElement?.replaceWith(em);
  });
  // Drop empty paragraphs except a single trailing one (the caret needs somewhere to live).
  const empties = Array.from(root.querySelectorAll("p")).filter((p) => p.textContent?.trim() === "" && !p.querySelector("br,img"));
  empties.slice(0, Math.max(0, empties.length - 1)).forEach((p) => p.remove());
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
